import { useState } from 'react';
import { ClarificationRequestItem as ClarificationRequestItemType } from '../../../types/conversation';
import { ClarificationResponse } from '../../ClarificationMessage';

interface ClarificationRequestItemProps {
  item: ClarificationRequestItemType;
  onSubmit?: (responses: ClarificationResponse[]) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  readOnly?: boolean;
}

export function ClarificationRequestItem({
  item,
  onSubmit,
  onCancel,
  isSubmitting = false,
  readOnly = false,
}: ClarificationRequestItemProps) {
  const [responses, setResponses] = useState<ClarificationResponse[]>(
    item.questions.map((_, index) => ({ questionIndex: index }))
  );
  const [showOther, setShowOther] = useState<boolean[]>(
    item.questions.map(() => false)
  );

  // In read-only mode, always show as completed
  const isCompleted = readOnly || item.status === 'answered';
  const displayResponses = item.responses || responses;

  const handleOptionSelect = (questionIndex: number, option: string) => {
    if (isCompleted) return;
    setResponses((prev) =>
      prev.map((r, i) =>
        i === questionIndex
          ? { questionIndex, selectedOption: option, customResponse: undefined }
          : r
      )
    );
    setShowOther((prev) => prev.map((v, i) => (i === questionIndex ? false : v)));
  };

  const handleOtherClick = (questionIndex: number) => {
    if (isCompleted) return;
    setShowOther((prev) => prev.map((v, i) => (i === questionIndex ? true : v)));
    setResponses((prev) =>
      prev.map((r, i) =>
        i === questionIndex
          ? { questionIndex, selectedOption: undefined, customResponse: '' }
          : r
      )
    );
  };

  const handleCustomInput = (questionIndex: number, value: string) => {
    if (isCompleted) return;
    setResponses((prev) =>
      prev.map((r, i) =>
        i === questionIndex
          ? { questionIndex, customResponse: value, selectedOption: undefined }
          : r
      )
    );
  };

  const isValid = responses.every(
    (r) => r.selectedOption || (r.customResponse && r.customResponse.trim())
  );

  const handleSubmit = () => {
    if (isValid && !isCompleted && onSubmit) {
      onSubmit(responses);
    }
  };

  const getAnswerText = (qIndex: number): string => {
    const response = displayResponses[qIndex];
    if (response?.selectedOption) {
      return response.selectedOption;
    }
    if (response?.customResponse) {
      return response.customResponse;
    }
    return '';
  };

  return (
    <div className="flex justify-start">
      <div className="max-w-[95%] w-full">
        <div className={`bg-white dark:bg-slate-800 rounded-2xl rounded-tl-sm border overflow-hidden ${
          isCompleted
            ? 'border-green-200 dark:border-green-800'
            : 'border-slate-200 dark:border-slate-700'
        }`}>
          {/* Header */}
          <div className={`px-4 py-3 border-b ${
            isCompleted
              ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
              : 'border-slate-200 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/20'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`p-1.5 rounded-lg ${
                isCompleted
                  ? 'bg-green-100 dark:bg-green-900/30'
                  : 'bg-blue-100 dark:bg-blue-900/30'
              }`}>
                {isCompleted ? (
                  <svg
                    className="w-5 h-5 text-green-600 dark:text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 text-blue-600 dark:text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  {isCompleted ? 'Wyjasnienia udzielone' : 'Potrzebuje wyjasnien'}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {isCompleted
                    ? 'Kontynuuje analize na podstawie Twoich odpowiedzi'
                    : 'Odpowiedz na pytania, aby uzyskac lepsza analize'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="px-4 py-4 space-y-5">
            {item.questions.map((q, qIndex) => (
              <div key={qIndex} className="space-y-2">
                <h4 className="font-medium text-sm text-slate-900 dark:text-white">
                  {qIndex + 1}. {q.question}
                </h4>

                {isCompleted ? (
                  // Completed state - show answer only
                  <div className="p-2.5 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                    <div className="flex items-start gap-2.5">
                      <svg
                        className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-sm text-green-700 dark:text-green-300">
                        {getAnswerText(qIndex)}
                      </span>
                    </div>
                  </div>
                ) : (
                  // Active state - show options
                  <div className="space-y-1.5">
                    {q.options.map((option, oIndex) => {
                      const isSelected = displayResponses[qIndex]?.selectedOption === option.label;
                      return (
                        <button
                          key={oIndex}
                          type="button"
                          onClick={() => handleOptionSelect(qIndex, option.label)}
                          disabled={isSubmitting}
                          className={`w-full text-left p-2.5 rounded-lg border transition-all text-sm ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                          }`}
                        >
                          <div className="flex items-start gap-2.5">
                            <div
                              className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected
                                  ? 'border-blue-500 bg-blue-500'
                                  : 'border-slate-300 dark:border-slate-500'
                              }`}
                            >
                              {isSelected && (
                                <svg
                                  className="w-2.5 h-2.5 text-white"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div
                                className={`font-medium ${
                                  isSelected
                                    ? 'text-blue-700 dark:text-blue-300'
                                    : 'text-slate-900 dark:text-white'
                                }`}
                              >
                                {option.label}
                              </div>
                              {option.description && (
                                <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                  {option.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}

                    {/* Other option */}
                    {q.allowOther !== false && (
                      <>
                        {!showOther[qIndex] ? (
                          <button
                            type="button"
                            onClick={() => handleOtherClick(qIndex)}
                            disabled={isSubmitting}
                            className="w-full text-left p-2.5 rounded-lg border border-dashed border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 transition-all"
                          >
                            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                />
                              </svg>
                              <span>Inna odpowiedz...</span>
                            </div>
                          </button>
                        ) : (
                          <div
                            className={`p-2.5 rounded-lg border ${
                              displayResponses[qIndex]?.customResponse
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-slate-200 dark:border-slate-600'
                            }`}
                          >
                            <textarea
                              value={displayResponses[qIndex]?.customResponse || ''}
                              onChange={(e) => handleCustomInput(qIndex, e.target.value)}
                              placeholder="Wpisz swoja odpowiedz..."
                              disabled={isSubmitting}
                              className="w-full bg-transparent text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-none focus:outline-none"
                              rows={2}
                              autoFocus
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer - only show when not completed */}
          {!isCompleted && onSubmit && onCancel && (
            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!isValid || isSubmitting}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Wysylanie...
                  </>
                ) : (
                  <>
                    Kontynuuj analize
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClarificationRequestItem;
